import { useMemo, useState } from 'react';
import { ListChecks, MessageSquareQuote, Plus, Search, Send, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { FeedbackEntryCard } from '@/components/customerFeedback/FeedbackEntryCard';
import { FeedbackEntryDialog } from '@/components/customerFeedback/FeedbackEntryDialog';
import { FeedbackTaskDialog } from '@/components/customerFeedback/FeedbackTaskDialog';
import { SendTaskRequestDialog } from '@/components/customerFeedback/SendTaskRequestDialog';
import {
  useCustomerFeedback,
  useDeleteCustomerFeedback,
  useDeleteFeedbackTask,
  useProcessCustomerFeedback,
  useSetFeedbackTaskStatus,
  type FeedbackTaskWithContext,
  type FeedbackWithContext,
} from '@/features/customerFeedback/hooks';
import {
  FEEDBACK_CHANNELS,
  FEEDBACK_CHANNEL_META,
  FEEDBACK_TASK_KINDS,
  FEEDBACK_TASK_KIND_META,
  describeFeedbackError,
  feedbackCustomerLabel,
} from '@/features/customerFeedback/model';
import { useAuth } from '@/app/AuthProvider';
import type { CustomerFeedbackChannel, CustomerFeedbackTaskKind } from '@/types/database';

type View = 'all' | 'review' | 'sent' | 'unprocessed';
type ChannelFilter = 'all' | CustomerFeedbackChannel;
type KindFilter = 'all' | CustomerFeedbackTaskKind;

const DAY_MS = 24 * 60 * 60 * 1000;

const VIEW_LABELS: Record<View, string> = {
  all: 'All',
  review: 'To review',
  sent: 'Requests sent',
  unprocessed: 'Not processed',
};

function needsReview(e: FeedbackWithContext): boolean {
  return (
    e.ai_status === 'failed' ||
    e.ai_status === 'pending' ||
    e.tasks.some((t) => t.status === 'suggested')
  );
}

/**
 * Customer Feedback — the Sales team's running log of what customers
 * are telling us. A rep pastes an interaction plus their own notes; the
 * extraction turns it into tasks, and "Send request" files each one as a
 * ticket for the team that can act on it (customization, a future
 * product, a customer-service follow-up, a quality concern).
 */
export function CustomerFeedbackPage() {
  const { profile, role } = useAuth();
  const canManage = role === 'super_admin' || role === 'admin' || role === 'manager';
  const { data, isLoading } = useCustomerFeedback();
  const entries = useMemo(() => data ?? [], [data]);

  const process = useProcessCustomerFeedback();
  const deleteEntry = useDeleteCustomerFeedback();
  const setTaskStatus = useSetFeedbackTaskStatus();
  const deleteTask = useDeleteFeedbackTask();

  const [view, setView] = useState<View>('all');
  const [channel, setChannel] = useState<ChannelFilter>('all');
  const [kind, setKind] = useState<KindFilter>('all');
  const [search, setSearch] = useState('');

  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<FeedbackWithContext | null>(null);
  const [taskDialog, setTaskDialog] = useState<{
    feedbackId: string;
    task: FeedbackTaskWithContext | null;
  } | null>(null);
  const [sending, setSending] = useState<{
    entry: FeedbackWithContext;
    task: FeedbackTaskWithContext;
  } | null>(null);

  const viewCounts = useMemo<Record<View, number>>(
    () => ({
      all: entries.length,
      review: entries.filter(needsReview).length,
      sent: entries.filter((e) => e.tasks.some((t) => t.status === 'sent')).length,
      unprocessed: entries.filter((e) => e.ai_status === 'pending').length,
    }),
    [entries],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (view === 'review' && !needsReview(e)) return false;
      if (view === 'sent' && !e.tasks.some((t) => t.status === 'sent')) return false;
      if (view === 'unprocessed' && e.ai_status !== 'pending') return false;
      if (channel !== 'all' && e.channel !== channel) return false;
      if (kind !== 'all' && !e.tasks.some((t) => t.kind === kind && t.status !== 'dismissed'))
        return false;
      if (q) {
        const hay = [
          feedbackCustomerLabel(e),
          e.customer?.email ?? '',
          e.interaction,
          e.rep_notes ?? '',
          e.ai_summary ?? '',
          e.author?.full_name ?? '',
          ...e.tasks.map((t) => `${t.title} ${t.details ?? ''}`),
        ]
          .join('\n')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [entries, view, channel, kind, search]);

  const metrics = useMemo(() => {
    const cutoff = Date.now() - 30 * DAY_MS;
    const recent = entries.filter((e) => new Date(e.occurred_at).getTime() >= cutoff);
    const allTasks = entries.flatMap((e) => e.tasks);
    return {
      interactions30d: recent.length,
      negative30d: recent.filter((e) => e.ai_sentiment === 'negative').length,
      toReview: allTasks.filter((t) => t.status === 'suggested').length,
      awaitingExtraction: viewCounts.unprocessed,
      sent: allTasks.filter((t) => t.status === 'sent').length,
      sentOpen: allTasks.filter(
        (t) =>
          t.status === 'sent' &&
          t.ticket &&
          !['done', 'declined', 'cancelled'].includes(t.ticket.status),
      ).length,
    };
  }, [entries, viewCounts.unprocessed]);

  const openCreate = () => {
    setEditingEntry(null);
    setEntryDialogOpen(true);
  };

  const runProcess = (entry: FeedbackWithContext) => {
    process.mutate(
      { feedbackId: entry.id },
      {
        onSuccess: (res) => {
          const n = res.tasks.length;
          toast.success(
            n === 0
              ? `${feedbackCustomerLabel(entry)}: nothing actionable found.`
              : `${feedbackCustomerLabel(entry)}: ${n} task${n === 1 ? '' : 's'} to review.`,
          );
        },
        onError: (err) => toast.error(`Extraction failed — ${describeFeedbackError(err)}`),
      },
    );
  };

  const handleDeleteEntry = (entry: FeedbackWithContext) => {
    const sentCount = entry.tasks.filter((t) => t.status === 'sent').length;
    const warning =
      sentCount > 0
        ? ` ${sentCount} ticket${sentCount === 1 ? ' was' : 's were'} filed from it and will stay open.`
        : '';
    if (!window.confirm(`Delete this interaction with ${feedbackCustomerLabel(entry)}?${warning}`))
      return;
    deleteEntry.mutate(entry.id, {
      onSuccess: () => toast.success('Entry deleted.'),
      onError: (err) => toast.error(describeFeedbackError(err)),
    });
  };

  const handleDeleteTask = (task: FeedbackTaskWithContext) => {
    if (!window.confirm(`Delete “${task.title}”? Dismissing keeps a record; deleting does not.`))
      return;
    deleteTask.mutate(task.id, {
      onError: (err) => toast.error(describeFeedbackError(err)),
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customer Feedback"
        meta="What customers are telling us, and what we are doing about it."
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" />
            Log interaction
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard
          label="Interactions (30 days)"
          value={metrics.interactions30d.toLocaleString()}
          icon={<MessageSquareQuote className="h-4 w-4 text-muted-foreground" />}
          hint={metrics.negative30d > 0 ? `${metrics.negative30d} negative` : undefined}
          loading={isLoading}
        />
        <MetricCard
          label="Tasks to review"
          value={metrics.toReview.toLocaleString()}
          icon={<ListChecks className="h-4 w-4 text-muted-foreground" />}
          hint="Suggested, not yet sent"
          loading={isLoading}
        />
        <MetricCard
          label="Awaiting extraction"
          value={metrics.awaitingExtraction.toLocaleString()}
          icon={<Sparkles className="h-4 w-4 text-muted-foreground" />}
          hint="Entries not processed yet"
          loading={isLoading}
        />
        <MetricCard
          label="Requests sent"
          value={metrics.sent.toLocaleString()}
          icon={<Send className="h-4 w-4 text-muted-foreground" />}
          hint={`${metrics.sentOpen} still open`}
          loading={isLoading}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={view} onValueChange={(v) => setView(v as View)}>
          <TabsList>
            {(Object.keys(VIEW_LABELS) as View[]).map((v) => (
              <TabsTrigger key={v} value={v} className="gap-1.5">
                {VIEW_LABELS[v]}
                <span className="rounded-full bg-secondary/80 px-1.5 py-0 text-[10px] font-medium tabular-nums text-muted-foreground">
                  {viewCounts[v]}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
          {(Object.keys(VIEW_LABELS) as View[]).map((v) => (
            <TabsContent key={v} value={v} className="hidden" />
          ))}
        </Tabs>

        <Select value={channel} onValueChange={(v) => setChannel(v as ChannelFilter)}>
          <SelectTrigger className="h-9 w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All channels</SelectItem>
            {FEEDBACK_CHANNELS.map((c) => (
              <SelectItem key={c} value={c}>
                {FEEDBACK_CHANNEL_META[c].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={kind} onValueChange={(v) => setKind(v as KindFilter)}>
          <SelectTrigger className="h-9 w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any kind of ask</SelectItem>
            {FEEDBACK_TASK_KINDS.map((k) => (
              <SelectItem key={k} value={k}>
                {FEEDBACK_TASK_KIND_META[k].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative ml-auto w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer, text, tasks…"
            className="h-9 pl-9"
            aria-label="Search feedback"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-56 w-full rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={MessageSquareQuote}
          title={
            entries.length === 0
              ? 'No customer interactions logged yet'
              : 'No interactions match these filters'
          }
          action={
            entries.length === 0 ? (
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4" />
                Log the first one
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-4">
          {filtered.map((entry) => (
            <FeedbackEntryCard
              key={entry.id}
              entry={entry}
              canDelete={canManage || entry.created_by === profile?.id}
              onEdit={(e) => {
                setEditingEntry(e);
                setEntryDialogOpen(true);
              }}
              onDelete={handleDeleteEntry}
              onProcess={runProcess}
              onAddTask={(e) => setTaskDialog({ feedbackId: e.id, task: null })}
              onEditTask={(e, t) => setTaskDialog({ feedbackId: e.id, task: t })}
              onSendTask={(e, t) => setSending({ entry: e, task: t })}
              onDismissTask={(t) =>
                setTaskStatus.mutate(
                  { id: t.id, status: 'dismissed' },
                  { onError: (err) => toast.error(describeFeedbackError(err)) },
                )
              }
              onRestoreTask={(t) =>
                setTaskStatus.mutate(
                  { id: t.id, status: 'suggested' },
                  { onError: (err) => toast.error(describeFeedbackError(err)) },
                )
              }
              onDeleteTask={handleDeleteTask}
            />
          ))}
        </div>
      )}

      <FeedbackEntryDialog
        open={entryDialogOpen}
        onOpenChange={(open) => {
          setEntryDialogOpen(open);
          if (!open) setEditingEntry(null);
        }}
        existing={editingEntry}
      />

      <FeedbackTaskDialog
        open={taskDialog !== null}
        onOpenChange={(open) => !open && setTaskDialog(null)}
        feedbackId={taskDialog?.feedbackId ?? null}
        existing={taskDialog?.task ?? null}
      />

      <SendTaskRequestDialog
        open={sending !== null}
        onOpenChange={(open) => !open && setSending(null)}
        entry={sending?.entry ?? null}
        task={sending?.task ?? null}
      />
    </div>
  );
}
